/**
 * @fileoverview Workspace mail settings (MOR-529).
 *
 * Global defaults that apply across every job the user works on:
 *  - Agent defaults (org-wide, MOR-854): the default persona and automation
 *    flags every job mailbox inherits; per-mailbox settings override them.
 *  - Notification defaults (CC me on outgoing / forward inbound to me) —
 *    the per-job Mail settings override these for individual jobs.
 *  - Custom sending domain (org-wide): full in-app lifecycle — register,
 *    publish DNS records, verify, set default, rotate DKIM, remove (restore
 *    on Inkbox only). `MAIL_PROVIDER=ses` runs it over SES identities (see
 *    SendingDomainsSection).
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { BellRing, Bot } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Switch } from "@/ui/primitives/switch";
import { Textarea } from "@/ui/primitives/textarea";
import { extractErrorMessage } from "@/utils/errors";
import { htmlToPlainText, plainTextToHtml } from "./htmlBody";
import {
  SendingDomainsSection,
  SettingsSection,
} from "./SendingDomainsSection";
import { SignatureEditor } from "./SignatureEditor";
import type { MailAgentDefaults, MailPrefs } from "./types";

export function WorkspaceMailSettings({
  orgId,
}: {
  orgId: Id<"organizations">;
}) {
  const mailInfo = useQuery(api.mail.queries.getMailProviderInfo, {});
  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 pb-10">
      <AgentDefaultsSection orgId={orgId} />
      <NotificationDefaultsSection />
      {/* Not drawn until the provider is known, so the vendor's wording never flashes. */}
      {mailInfo ? (
        <SendingDomainsSection orgId={orgId} provider={mailInfo.provider} />
      ) : null}
    </div>
  );
}

/**
 * Org-wide agent persona defaults (MOR-854). Every job mailbox inherits
 * these unless its own Mail settings override a field. Remounts the form
 * whenever the saved defaults change so state re-seeds from fresh data.
 */
function AgentDefaultsSection({ orgId }: { orgId: Id<"organizations"> }) {
  const data = useQuery(api.mail.orgDefaults.getMailAgentDefaults, {
    orgId,
  });
  if (!data) return null;
  return (
    <AgentDefaultsForm
      key={JSON.stringify(data.defaults)}
      data={data}
      orgId={orgId}
    />
  );
}

function AgentDefaultsForm({
  data,
  orgId,
}: {
  data: MailAgentDefaults;
  orgId: Id<"organizations">;
}) {
  const updateDefaults = useMutation(
    api.mail.orgDefaults.updateMailAgentDefaults,
  );
  const saved = data.defaults;

  const [agentName, setAgentName] = useState(saved.agentName ?? "");
  const [senderName, setSenderName] = useState(saved.senderName ?? "");
  const [agentTone, setAgentTone] = useState(saved.agentTone ?? "");
  const [agentInstructions, setAgentInstructions] = useState(
    saved.agentInstructions ?? "",
  );
  // One HTML value backs the signature editor; legacy text-only signatures
  // are lifted to HTML so they open editable. Save writes BOTH parts (the
  // HTML plus derived plain text) — the send pipeline appends one per part.
  const savedSignatureHtml =
    saved.signatureHtml ??
    (saved.signatureText ? plainTextToHtml(saved.signatureText) : "");
  const [signatureHtml, setSignatureHtml] = useState(savedSignatureHtml);
  const [autoDraftReplies, setAutoDraftReplies] = useState(
    data.effective.autoDraftReplies,
  );
  const [autoFollowUps, setAutoFollowUps] = useState(
    data.effective.autoFollowUps,
  );
  const [saving, setSaving] = useState(false);

  const dirty = useMemo(
    () =>
      agentName !== (saved.agentName ?? "") ||
      senderName !== (saved.senderName ?? "") ||
      agentTone !== (saved.agentTone ?? "") ||
      agentInstructions !== (saved.agentInstructions ?? "") ||
      signatureHtml !== savedSignatureHtml ||
      autoDraftReplies !== data.effective.autoDraftReplies ||
      autoFollowUps !== data.effective.autoFollowUps,
    [
      agentName,
      senderName,
      agentTone,
      agentInstructions,
      signatureHtml,
      savedSignatureHtml,
      autoDraftReplies,
      autoFollowUps,
      saved,
      data.effective,
    ],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDefaults({
        orgId,
        agentName: agentName.trim() || null,
        senderName: senderName.trim() || null,
        agentTone: agentTone.trim() || null,
        agentInstructions: agentInstructions.trim() || null,
        signatureHtml: signatureHtml.trim() || null,
        signatureText: htmlToPlainText(signatureHtml).trim() || null,
        autoDraftReplies,
        autoFollowUps,
      });
      toast.success("Agent defaults saved");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to save agent defaults"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection
      icon={Bot}
      title="Agent defaults"
      description="Applies to every job mailbox in your workspace. Each mailbox's own settings can override any field for that job."
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="default-agent-name">Agent name</Label>
          <Input
            id="default-agent-name"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            maxLength={60}
            placeholder={data.fallbackAgentName}
          />
          <p className="text-xs text-muted-foreground">
            Used when Morgan drafts and signs replies.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="default-agent-tone">Tone</Label>
          <Input
            id="default-agent-tone"
            value={agentTone}
            onChange={(e) => setAgentTone(e.target.value)}
            placeholder="e.g. formal, warm, concise"
          />
          <p className="text-xs text-muted-foreground">
            Optional style guidance for drafts.
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="default-sender-name">Sender name</Label>
        <Input
          id="default-sender-name"
          value={senderName}
          onChange={(e) => setSenderName(e.target.value)}
          maxLength={100}
          placeholder={`${agentName.trim() || data.effective.agentName} — company name`}
        />
        <p className="text-xs text-muted-foreground">
          The From name recipients see on outgoing mail. Leave blank for the
          default: agent name, a dash, then each job's company name.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="default-agent-instructions">Instructions</Label>
        <Textarea
          id="default-agent-instructions"
          value={agentInstructions}
          onChange={(e) => setAgentInstructions(e.target.value)}
          rows={5}
          maxLength={5000}
          placeholder="Guidance for how the agent should handle mail on every job."
        />
      </div>

      <div className="space-y-1.5">
        <Label>Signature</Label>
        <SignatureEditor
          idPrefix="default"
          value={signatureHtml}
          onChange={setSignatureHtml}
        />
        <p className="text-xs text-muted-foreground">
          Appended to every outgoing email across your workspace. Paste a
          signature from Outlook, Gmail, or Word into the editor, or type and
          format it here.
        </p>
      </div>

      <div className="flex items-start justify-between gap-4 rounded-lg border px-4 py-3">
        <div className="space-y-0.5">
          <Label htmlFor="default-auto-draft">Auto-draft replies</Label>
          <p className="text-xs text-muted-foreground">
            When on, inbound mail triggers Morgan to draft a reply for review.
          </p>
        </div>
        <Switch
          id="default-auto-draft"
          checked={autoDraftReplies}
          onCheckedChange={setAutoDraftReplies}
        />
      </div>

      <div className="flex items-start justify-between gap-4 rounded-lg border px-4 py-3">
        <div className="space-y-0.5">
          <Label htmlFor="default-auto-follow-ups">Proactive follow-ups</Label>
          <p className="text-xs text-muted-foreground">
            Morgan periodically reviews unanswered threads and prepares
            follow-up drafts for approval. Nothing sends without you.
          </p>
        </div>
        <Switch
          id="default-auto-follow-ups"
          checked={autoFollowUps}
          onCheckedChange={setAutoFollowUps}
        />
      </div>

      <div className="flex justify-end border-t pt-4">
        <Button onClick={handleSave} disabled={!dirty || saving}>
          {saving ? "Saving..." : "Save defaults"}
        </Button>
      </div>
    </SettingsSection>
  );
}

/** Global CC/forward defaults — per-job Mail settings override these. */
function NotificationDefaultsSection() {
  const prefs = useQuery(api.mail.ccRules.getMyMailPrefs, {}) as
    MailPrefs | null | undefined;
  const setGlobalMailPrefs = useMutation(api.mail.ccRules.setGlobalMailPrefs);
  const [saving, setSaving] = useState(false);

  const ccOutbound = prefs?.global?.ccOutbound ?? false;
  const forwardInbound = prefs?.global?.forwardInbound ?? false;

  const save = async (next: {
    ccOutbound: boolean;
    forwardInbound: boolean;
  }) => {
    setSaving(true);
    try {
      await setGlobalMailPrefs(next);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to update defaults"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection
      icon={BellRing}
      title="Notification defaults"
      description="Applies across every job you work on. Each job's Mail settings can override these for that job."
    >
      <div className="flex items-start justify-between gap-4 rounded-lg border px-4 py-3">
        <div className="space-y-0.5">
          <Label htmlFor="global-cc">CC me on outgoing mail</Label>
          <p className="text-xs text-muted-foreground">
            You are added to the Cc of every email your job mailboxes send.
          </p>
        </div>
        <Switch
          id="global-cc"
          checked={ccOutbound}
          disabled={saving || prefs === undefined}
          onCheckedChange={(checked) =>
            void save({ ccOutbound: checked, forwardInbound })
          }
        />
      </div>

      <div className="flex items-start justify-between gap-4 rounded-lg border px-4 py-3">
        <div className="space-y-0.5">
          <Label htmlFor="global-forward">
            Forward incoming mail to my inbox
          </Label>
          <p className="text-xs text-muted-foreground">
            Every inbound message on your job mailboxes is forwarded to your
            account email.
          </p>
        </div>
        <Switch
          id="global-forward"
          checked={forwardInbound}
          disabled={saving || prefs === undefined}
          onCheckedChange={(checked) =>
            void save({ ccOutbound, forwardInbound: checked })
          }
        />
      </div>
    </SettingsSection>
  );
}
